import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {ReactComponent as MinusIco} from "../../../../../../../../images/icons/minus.svg";
import MenuProductModal from "../../../MenuProductModal";
import ItemHelper from "../../../../../../../services/Menu/services/Item/ItemHelper";
import {connect} from "react-redux";
import {RootState} from "../../../../../../../../reducers";
import {getItems} from "../../../../../../../services/Menu/reducer";
import {getStoreConfig} from "../../../../../../../services/Store/reducer";
import StoreUtils from "../../../../../../../services/Store/StoreUtils";

interface OwnProps {
    modifierGroupOption: Record<string, any>;
    option: Record<string, any>;
    options: Record<string, any>[];
    position: number | undefined;
    handleAdjustOption: (state: Record<string, any>, stateIndex: number) => void;
    handleRemoveOption: ((state: Record<string, any>) => void) | undefined;
    isProductAvailable?: boolean;
    preventShowModalOnCheckingConfiguration?: true;
    menuId?: number;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductModifierGroupAdvancedSubOption = ({
                                                   modifierGroupOption,
                                                   option,
                                                   options,
                                                   position,
                                                   items,
                                                   config,
                                                   handleAdjustOption,
                                                   handleRemoveOption,
                                                   isProductAvailable,
                                                   preventShowModalOnCheckingConfiguration,
                                                   menuId
                                               }: Props) => {
    const {t} = useTranslation();
    const [showProductModal, setShowProductModal] = useState(false);
    const isReadonly = StoreUtils.isOrderingReadonly(config);

    useEffect(() => {
        if (preventShowModalOnCheckingConfiguration || isReadonly) return;

        handleForceAdjustOption();
    }, []);

    const isNotFullyConfigured = (item: Record<string, any>) => {
        return modifierGroupOption.modifier_groups.some((modifierGroupOptionItem: Record<string, any>) => {
            const minPermitted = modifierGroupOptionItem.min_permitted ?? 0;
            const itemModifierGroup = item.modifier_groups.find((itemModifierGroup: Record<string, any>) => itemModifierGroup.modifier_group_id === modifierGroupOptionItem.id);

            if (!itemModifierGroup) {
                return minPermitted > 0;
            }
            const selectedItems = itemModifierGroup.selected_items ?? [];

            const selectedItemsQuantity = selectedItems.reduce((acc: number, item: Record<string, any>) => acc + item.quantity, 0);

            return selectedItemsQuantity < minPermitted;
        });
    };

    const handleForceAdjustOption = () => {
        if (isNotFullyConfigured(option)) {
            setShowProductModal(true);
        }
    };

    const handleHideModal = (item?: Record<string, any>) => {
        if (item && isNotFullyConfigured(item) && handleRemoveOption) {
            handleRemoveOption(option);
        }
    };

    const getSortedSelectedOptions = () => {
        const sortedOptions = [...options].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
        const order = sortedOptions.map(option => option.id);

        return option.modifier_groups.filter((optionModifierGroup: Record<string, any>) => {
            return !!sortedOptions.find((sortedOption) => sortedOption.id === optionModifierGroup.modifier_group_id);
        }).sort((a: Record<string, any>, b: Record<string, any>) => order.indexOf(a.modifier_group_id) - order.indexOf(b.modifier_group_id));
    };

    const getOptionsInfo = () => {
        return getSortedSelectedOptions().flatMap((option: Record<string, any>) => option.selected_items.map((item: Record<string, any>) => {
            if (item.quantity > 1) {
                return `${item.name} x${item.quantity}`;
            }

            return item.name;
        })).join(", ");
    };

    const isAdjustButtonVisible = () => {
        if (isReadonly) return false;

        return modifierGroupOption.is_customize;
    }

    return (
        <>
            {position && (
                <hr/>
            )}
            <div className="product-modifier-group-advanced-sub-option">
                {position && (
                    <h1 className="h6 fw-normal mb-1">
                        {`#${position}`}
                    </h1>
                )}
                <p className="fw-normal">
                    {getOptionsInfo()}
                </p>
                {isAdjustButtonVisible() && isNotFullyConfigured(option) && preventShowModalOnCheckingConfiguration &&
                    <p className="fw-normal mt-2 mb-0 text-danger">
                        {t("constraints.internal.configuration_needed")}
                    </p>}
                {
                    (isAdjustButtonVisible() || handleRemoveOption) && (
                        <div className="buttons-container d-flex w-100 gap-3">
                            {
                                isAdjustButtonVisible() && (
                                    <button className="btn btn-default w-100" onClick={() => setShowProductModal(true)}
                                            disabled={isProductAvailable === false}>
                                        {t("modules.menu.action.adjust.title")}
                                    </button>
                                )
                            }
                            {
                                handleRemoveOption && !isReadonly && (
                                    <button type="button" className="btn btn-default btn-icon"
                                            onClick={() => handleRemoveOption(option)}
                                            disabled={option.quantity <= modifierGroupOption.min_permitted}
                                            aria-label={`${t("common.action.remove")} ${option.name} ${getOptionsInfo()}`}>
                                        <MinusIco aria-hidden="true"/>
                                    </button>
                                )
                            }
                        </div>
                    )
                }
            </div>
            {showProductModal && (
                <MenuProductModal item={ItemHelper.getItem(items, option.item_id)}
                                  itemGroup={undefined}
                                  show={showProductModal}
                                  setShow={setShowProductModal}
                                  nested
                                  externalItemState={option}
                                  customOnSubmit={handleAdjustOption}
                                  config={config}
                                  onHide={handleHideModal}
                                  menuId={menuId}
                />
            )}
        </>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    config: getStoreConfig(state.config),
})

const connector = connect(mapStateToProps);
export default connector(ProductModifierGroupAdvancedSubOption);